import { useEffect, useState, type ReactNode } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { setSEO, type SEOConfig } from '../../utils/seo';

interface LegalSection {
  title: string;
  content: ReactNode;
}

interface LegalPageLayoutProps {
  badge: string;
  title: string;
  subtitle: string;
  lastUpdated: string;
  sections: LegalSection[];
  seo: SEOConfig;
}

export default function LegalPageLayout({
  badge,
  title,
  subtitle,
  lastUpdated,
  sections,
  seo,
}: LegalPageLayoutProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setSEO(seo);
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [seo.title, seo.description, seo.canonicalUrl, seo.keywords]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative bg-white pt-28 md:pt-32 pb-10 border-b border-gray-100">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-transparent to-transparent pointer-events-none" />
        <div
          className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand border border-brand-blue/20 bg-blue-50/80 px-4 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand" />
            {badge}
          </span>
          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-gray-900 leading-tight mb-4">
            {title}
          </h1>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-3">{subtitle}</p>
          <p className="text-gray-400 text-sm">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 md:p-10 shadow-sm space-y-10">
            {sections.map((section) => (
              <div key={section.title}>
                <h2 className="font-display font-semibold text-xl text-gray-900 mb-3">
                  {section.title}
                </h2>
                <div className="text-gray-600 text-[15px] leading-relaxed space-y-3">
                  {section.content}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-gray-500 text-sm">
            Questions? Contact us at{' '}
            <a href="mailto:info@blueglobemedia.com" className="text-brand hover:underline">
              info@blueglobemedia.com
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
